import { useState } from "react";
import { cancelInvitationAsync, resendInvitationAsync, createInvitationAsync } from "@features/invitations/services/invitationSvc";
import { SendInvitationRequest } from "@features/invitations/types/sendInvitationRequest";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from '@core/types/errorResponse';

/**
 * Custom React hook that provides actions for managing invitations, including canceling,
 * resending, and creating invitations. It also manages a busy state and handles API errors.
 *
 * @returns An object containing:
 * - `busy`: A boolean indicating if an action is in progress.
 * - `doCancel`: Function to cancel an invitation by ID and optional scope data.
 * - `doResend`: Function to resend an invitation by ID and optional scope data.
 * - `doCreate`: Function to create a new invitation with the provided data.
 */
export function useInviteActions() {
    const [busy, setBusy] = useState(false);
    const showError = useApiError();

    /**
     * Cancels an invitation asynchronously by its ID and optional scope data.
     *
     * Sets the busy state while the operation is in progress. On success, displays a success message.
     * On failure, displays an error message. Always resets the busy state when finished.
     *
     * @param id - The unique identifier of the invitation to cancel.
     * @param data - Optional object containing scope and scope reference ID.
     * @returns A promise that resolves to the response of the cancellation operation.
     * @throws Rethrows any error encountered during the cancellation process.
     */
    const doCancel = async (id: number, data: { scope: string; scope_ref_id: number }) => {
        setBusy(true);
        try {
            const response = await cancelInvitationAsync(id, data);
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message);
            } else {
                showError(response.getError() as ErrorResponse);
            }
            return response;
        } catch (error) {
            showError(error as ErrorResponse);
            throw error;
        } finally {
            setBusy(false);
        }
    };

    /**
     * Resends an invitation by its ID, optionally with additional scope data.
     *
     * Sets the busy state while the operation is in progress. On success, displays a success message.
     * On failure, displays an error message. Always resets the busy state after completion.
     *
     * @param id - The unique identifier of the invitation to resend.
     * @param data - Optional object containing scope and scope_ref_id for the invitation.
     * @returns A promise that resolves to the response of the resend operation.
     * @throws Rethrows any error encountered during the resend operation after displaying an error message.
     */
    const doResend = async (id: number, data: { scope: string; scope_ref_id: number }) => {
        setBusy(true);
        try {
            const response = await resendInvitationAsync(id, data);
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message);
            } else {
                showError(response.getError() as ErrorResponse);
            }
            return response;
        } catch (error) {
            showError(error as ErrorResponse);
            throw error;
        } finally {
            setBusy(false);
        }
    };

    /**
     * Asynchronously creates a new invitation using the provided data.
     * 
     * Sets the busy state to `true` before initiating the request and resets it to `false` after the operation completes.
     * Returns the response from the invitation creation API.
     *
     * @param data - The invitation details to be sent in the request.
     * @returns A promise that resolves with the response from the invitation creation API.
     */
    const doCreate = async (scope: string, scopeRefId: number, data: SendInvitationRequest) => {
        setBusy(true);
        const response = await createInvitationAsync(scope, scopeRefId, data);
        setBusy(false);
        return response;
    };

    return { busy, doCancel, doResend, doCreate };
}
